import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../components/layout/AuthLayout";
import Button from "../components/common/Button";
import { loginAgent, fetchCurrentAgent } from "../lib/agentAuth";
import { getAgentHomePath } from "../lib/agentOnboarding";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { BRAND } from "../lib/brand";
import { getAgentToken } from "../lib/tokenStore";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;

    async function restoreSession() {
      if (!getAgentToken()) {
        return;
      }

      try {
        const profile = await fetchCurrentAgent();
        if (alive) {
          navigate(getAgentHomePath(profile), { replace: true });
        }
      } catch (sessionError) {
        if (alive) {
          showErrorAction("SESSION_EXPIRED");
        }
      }
    }

    restoreSession();

    return () => {
      alive = false;
    };
  }, [navigate]);

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);

    try {
      const result = await loginAgent({
        email,
        password,
      });

      const partner = result?.partner || {};
      showSuccessAction("LOGIN");
      navigate(getAgentHomePath(partner), { replace: true });
    } catch (loginError) {
      showApiError(loginError, "LOGIN_FAILED");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      heroImage="/template/assets/images/auth/agent_way2umrah.png"
      heroAlt="Welcome to Way2Umrah Agent Portal"
    >
      <div className="max-w-464-px mx-auto w-100 auth-form-card">
        <Link to="/dashboard" className="mb-40 max-w-290-px d-inline-block">
          <img src={BRAND.logo} alt={BRAND.name} className="auth-brand-logo" />
        </Link>
        <h4 className="mb-12">Agent Portal sign in</h4>
        {/* <p className="mb-24 text-secondary-light text-lg">
          Use your verified travel-partner credentials to access the live portal.
        </p>
        <div className="alert alert-info bg-info-600 text-white border-info-600 px-24 py-11 mb-24 fw-semibold text-lg radius-8" role="alert">
          Demo navigation has been replaced with backend authentication.
        </div> */}
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="login-email">
              Email
            </label>
            <input
              type="email"
              className="form-control h-56-px bg-neutral-50 radius-12"
              id="login-email"
              placeholder="name@agency.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>
          <div className="mb-16">
            <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="password">
              Password
            </label>
            <div className="icon-field auth-password-field">
              <span className="icon top-50 translate-middle-y">
                <iconify-icon icon="solar:lock-password-outline"></iconify-icon>
              </span>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control h-56-px bg-neutral-50 radius-12 auth-password-input"
                id="password"
                placeholder="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <button
                type="button"
                className={`auth-password-toggle toggle-password ri-${showPassword ? "eye-off-line" : "eye-line"}`}
                onClick={() => setShowPassword((value) => !value)}
                aria-label="Toggle password visibility"
              />
            </div>
          </div>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 auth-form-meta">
            <div className="text-secondary-light text-sm min-w-0">
              Accounts must be active before dashboard access. Pending agencies can check approval status after sign in.
            </div>
            <Link to="/forgot-password" className="text-primary-600 fw-medium text-nowrap">
              Forgot password?
            </Link>
          </div>
          <Button
            type="submit"
            className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-24"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
          <div className="mt-24 text-center text-sm">
            <p className="mb-0">
              Need an agency account? <Link to="/register" className="text-primary-600 fw-semibold">Register</Link>
            </p>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
